import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useBranding, resolveImageUrl } from '@/contexts/BrandingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Shield } from 'lucide-react';

const DEFAULT_HERO_IMAGE = "https://images.pexels.com/photos/19737883/pexels-photo-19737883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function HeroSection({ brandData, stats, searchQuery, onSearchChange }) {
  const { t, locale } = useI18n();
  const heroImage = brandData?.hero_image_url
    ? resolveImageUrl(brandData.hero_image_url)
    : DEFAULT_HERO_IMAGE;

  // Helper: use brandData text only in Spanish, otherwise use i18n
  const bt = (brandField, i18nKey, params) => {
    if (locale === 'es' && brandField) return brandField;
    return t(i18nKey, params);
  };

  return (
    <section className="relative h-[90vh] min-h-[650px] flex items-end pb-16">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-2xl animate-fade-up">
          <p className="font-serif italic text-white/80 text-sm mb-3 tracking-wide">
            {bt(brandData?.hero_subtitle, 'landing.hero.title')}
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tighter leading-[0.95] mb-6"
            style={{ fontFamily: 'var(--font-heading, Outfit)' }}
          >
            {bt(brandData?.hero_title, 'landing.hero.title')}
          </h1>
          <p className="text-base text-white/75 mb-10 leading-relaxed max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {(bt(brandData?.hero_description, 'landing.hero.desc', { properties: stats.properties, cities: stats.cities }))
              .replace('{properties}', stats.properties)
              .replace('{cities}', stats.cities)}
          </p>
        </div>

        {/* Floating Search Bar */}
        <div className="animate-fade-up stagger-2 bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-2xl max-w-2xl border border-white/40">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
              <Input
                type="text"
                placeholder={t('landing.hero.searchPlaceholder')}
                className="pl-11 h-12 border-[#D5D8CF] text-base"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                data-testid="hero-search-input"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              />
            </div>
            <Link to={`/properties${searchQuery ? `?search=${searchQuery}` : ''}`}>
              <Button className="w-full sm:w-auto px-8 h-12 text-white text-base" style={{ backgroundColor: 'var(--brand-primary, #264639)' }} data-testid="hero-search-btn">
                {t('landing.search.button')}
              </Button>
            </Link>
          </div>
          <p className="text-xs text-[#78716C] mt-3 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <Shield className="w-3.5 h-3.5" /> {t('landing.hero.secureBookings', { count: stats.properties })}
          </p>
        </div>
      </div>
    </section>
  );
}
